import {
	type CSSProperties,
	type ElementType,
	type ReactNode,
	useEffect,
	useState,
} from "react";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";

export type MarketingAnimatedProps = {
	children: ReactNode;
	className?: string;
	/** Delay before the entrance animation starts (ms). */
	delayMs?: number;
	as?: ElementType;
	style?: CSSProperties;
};

/**
 * Runs marketing entrance animations after mount so they replay on client
 * navigation and are not skipped when HTML is prerendered.
 */
export function MarketingAnimated({
	children,
	className,
	delayMs = 0,
	as: Component = "div",
	style,
}: MarketingAnimatedProps) {
	const [entered, setEntered] = useState(false);

	useEffect(() => {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			setEntered(true);
			return;
		}
		const frame = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(frame);
	}, []);

	const animStyle: CSSProperties | undefined =
		entered && delayMs > 0
			? { animationDelay: `${delayMs}ms`, ...style }
			: style;

	return (
		<Component
			className={cn(
				entered
					? cn(MARKETING_SECTION_ANIM_BASE, className)
					: "opacity-0 motion-reduce:opacity-100",
			)}
			style={animStyle}
		>
			{children}
		</Component>
	);
}
